import { useCallback, useEffect, useReducer, useRef } from 'react';

import type { ChatMessage, DeveloperStatusStep } from '../../services/chatService';
import { eventService } from '../../services/eventService';
import { projectService } from '../../services/projectService';
import type { SupervisorButton } from '../../types/supervisorButtons';
import { isRewardClaimReceipt } from '../../utils/rewardClaimMessage';

import { latestUserMessageHasDocumentAttachments } from './documentAttachments';
import {
  mergePendingSuggestedActionMetadata,
  suggestedAgentsEventKey,
  SuggestedAgentsEventDetail,
} from './suggestionUtils';
import { ChatState, StreamingRefsFull, StreamingState } from './types';
import { usePlanningMessageRecovery } from './usePlanningMessageRecovery';

const mergeMessageActionMetadata = (
  current: ChatMessage,
  incoming: ChatMessage,
): ChatMessage => ({
  ...current,
  ...incoming,
  phase: incoming.phase ?? current.phase ?? null,
  allowed_buttons: incoming.allowed_buttons ?? current.allowed_buttons ?? null,
  buttons: incoming.buttons ?? current.buttons ?? null,
  clicked_button: incoming.clicked_button ?? current.clicked_button ?? null,
  clicked_buttons: incoming.clicked_buttons ?? current.clicked_buttons ?? null,
  prompt_scope: incoming.prompt_scope ?? current.prompt_scope ?? null,
});

const useSuggestedActionMetadataSubscription = (
  projectId: string | null,
  activeSessionId: string | null,
  chatState: ChatState,
) => {
  const { messages, setMessages } = chatState;
  const pendingMetadataRef = useRef(new Map<string, SuggestedAgentsEventDetail>());
  const [pendingVersion, markPendingChanged] = useReducer((version: number) => version + 1, 0);

  useEffect(() => {
    const pendingMetadata = pendingMetadataRef.current;

    pendingMetadata.clear();

    return () => pendingMetadata.clear();
  }, [activeSessionId, projectId]);

  useEffect(() => {
    if (!projectId) return undefined;

    const handler = (event: Event) => {
      const detail = (event as globalThis.CustomEvent).detail as SuggestedAgentsEventDetail;

      if (detail.projectId !== projectId) return;

      if (activeSessionId && detail.sessionId && detail.sessionId !== activeSessionId) return;

      if (!detail.messageId || !Array.isArray(detail.buttons) || detail.buttons.length === 0) return;

      pendingMetadataRef.current.set(suggestedAgentsEventKey(detail), detail);
      markPendingChanged();
    };

    window.addEventListener('suggested_agents', handler);

    return () => window.removeEventListener('suggested_agents', handler);
  }, [activeSessionId, projectId]);

  useEffect(() => {
    const result = mergePendingSuggestedActionMetadata(
      messages,
      pendingMetadataRef.current,
    );

    if (result.matchedKeys.length === 0) return;

    result.matchedKeys.forEach((key) => pendingMetadataRef.current.delete(key));
    if (result.messages !== messages) setMessages(result.messages);
  }, [messages, pendingVersion, setMessages]);
};

// Subscribe to real-time system chat messages (planning status) via SSE
const useSystemMessageSubscription = (
  projectId: string | null,
  activeSessionId: string | null,
  chatState: ChatState,
) => {
  useEffect(() => {
    if (!projectId) return undefined;

    const unsubscribe = eventService.subscribe('chat_message', (data: unknown) => {
      const payload = data as {
        type: string; id: string; project_id: string; session_id?: string | null;
        content: string; author: string; user_id?: string; created_at: string;
        phase?: string | null; allowed_buttons?: string[] | null;
        buttons?: SupervisorButton[] | null; prompt_scope?: Record<string, unknown> | null;
        clicked_button?: string | null; clicked_buttons?: string[] | null;
      };

      if (payload.project_id !== projectId) return;
      // No session_id means a project-scoped broadcast (e.g. system messages
      // enqueued without a session) — accept it, same as the stream matcher below.
      if (activeSessionId && payload.session_id && payload.session_id !== activeSessionId) return;

      const newMsg: ChatMessage = {
        id: payload.id,
        user_id: payload.user_id ?? '',
        project_id: payload.project_id,
        session_id: payload.session_id ?? null,
        content: payload.content,
        author: payload.author,
        created_at: payload.created_at,
        updated_at: payload.created_at,
        phase: payload.phase ?? null,
        allowed_buttons: payload.allowed_buttons ?? null,
        buttons: payload.buttons ?? null,
        clicked_button: payload.clicked_button ?? null,
        clicked_buttons: payload.clicked_buttons ?? null, prompt_scope: payload.prompt_scope ?? null,
      };

      chatState.setMessages((prev: ChatMessage[]) => {
        const existing = prev.find((m) => m.id === newMsg.id);
        if (existing) {
          return prev.map((m) => (
            m.id === newMsg.id ? mergeMessageActionMetadata(m, newMsg) : m
          ));
        }

        return [...prev, newMsg];
      });
    });

    return unsubscribe;
  }, [activeSessionId, projectId, chatState]);
};

// Subscribe to server-driven chat stream events (start/delta/done).
// These drive the "typing" bubble for assistant messages that originate on the
// server: developer-mode flows (plan approval, file syncs, doc indexing) and the
// Tech Lead's scope narration during planning. They share the typing slot with
// the user's own reply ticker, which streams with a null id until it finalises,
// so a server stream never claims the slot while that is in flight and only
// the stream that owns the slot (matching id) may append to it or close it.
type ServerStreamPayload = {
  project_id?: string | null;
  session_id?: string | null;
  id?: string;
  content?: string;
};

type ServerStreamOwner = {
  /** id of the server stream currently owning the typing slot (set synchronously
   *  in the `start` handler so back-to-back deltas never see a stale value). */
  ownedIdRef: React.MutableRefObject<string | null>;
  userReplyInFlight: () => boolean;
};

const releaseServerStreamSlot = (streamingState: StreamingState, owner: ServerStreamOwner) => {
  // A lost `done` (reconnect, worker restart) must not leave the bubble typing
  // forever. The user's own ticker (no server owner) is left alone.
  if (!owner.ownedIdRef.current) return;
  owner.ownedIdRef.current = null;
  streamingState.setIsAiTyping(false);
  streamingState.setStreamingDisplay('');
  streamingState.setCurrentStreamingMsgId(null);
};

const subscribeServerStreams = (
  projectId: string,
  activeSessionId: string | null,
  streamingState: StreamingState,
  owner: ServerStreamOwner,
): (() => void) => {
  const matches = (p: ServerStreamPayload) => (
    p.project_id === projectId
    && (!activeSessionId || !p.session_id || p.session_id === activeSessionId)
  );
  const ownsSlot = (p: ServerStreamPayload) => !!p.id && owner.ownedIdRef.current === p.id;
  const unsubscribers = [
    eventService.subscribe('chat_message_stream_start', (data: unknown) => {
      const p = data as ServerStreamPayload;

      if (!matches(p) || !p.id || owner.userReplyInFlight()) return;
      owner.ownedIdRef.current = p.id;
      streamingState.setStreamingDisplay('');
      streamingState.setCurrentStreamingMsgId(p.id);
      streamingState.setIsAiTyping(true);
    }),
    eventService.subscribe('chat_message_stream_delta', (data: unknown) => {
      const p = data as ServerStreamPayload;

      if (matches(p) && ownsSlot(p) && p.content) streamingState.appendStreamingDisplay(p.content);
    }),
    eventService.subscribe('chat_message_stream_done', (data: unknown) => {
      const p = data as ServerStreamPayload;

      if (matches(p) && ownsSlot(p)) releaseServerStreamSlot(streamingState, owner);
    }),
    eventService.subscribe('planning_complete', (data: unknown) => {
      const p = data as ServerStreamPayload;

      if (!p.project_id || p.project_id === projectId) releaseServerStreamSlot(streamingState, owner);
    }),
    eventService.subscribe('connected', () => releaseServerStreamSlot(streamingState, owner)),
  ];

  return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
};

const useDeveloperStreamSubscription = (
  projectId: string | null,
  activeSessionId: string | null,
  streamingState: StreamingState,
  refsFull: StreamingRefsFull,
) => {
  const ownedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!projectId) return undefined;
    const owner: ServerStreamOwner = {
      ownedIdRef,
      // The user's own reply owns the slot from send until its ticker drains.
      userReplyInFlight: () => (
        refsFull.isUserStreamingRef.current || refsFull.streamControllerRef.current !== null
      ),
    };

    return subscribeServerStreams(projectId, activeSessionId, streamingState, owner);
  }, [projectId, activeSessionId, streamingState, refsFull]);
};

// Defensive cleanup: if a stream-done event was lost (network blip, race with
// the persisted chat_message arriving first, worker crash), clear the typing
// indicator whenever developer mode reports the run is terminal.
const useDeveloperStatusCleanup = (
  projectId: string | null,
  activeSessionId: string | null,
  streamingState: StreamingState,
) => {
  useEffect(() => {
    if (!projectId) return undefined;

    const TERMINAL = new Set([
      'complete', 'completed', 'failed', 'idle', 'cancelled', 'needs_plan_feedback',
    ]);

    return eventService.subscribe('developer_mode_status', (data: unknown) => {
      const p = data as { project_id?: string | null; session_id?: string | null; status?: string | null };
      if (p.project_id !== projectId) return;
      if (activeSessionId && p.session_id && p.session_id !== activeSessionId) return;
      const status = (p.status || '').toLowerCase();
      if (!TERMINAL.has(status)) return;
      streamingState.setIsAiTyping(false);
      streamingState.setStreamingDisplay('');
      streamingState.setCurrentStreamingMsgId(null);
    });
  }, [projectId, activeSessionId, streamingState]);
};

const TERMINAL_DEVELOPER_STATUSES = new Set([
  'complete', 'completed', 'failed', 'idle', 'cancelled', 'canceled', 'needs_plan_feedback', 'stopped',
]);
const ACTIVE_DEVELOPER_STATUSES = new Set(['running', 'syncing', 'indexing']);
const DEVELOPER_PROGRESS_DISMISS_DELAY_MS = 5000;

const timestampMs = (value?: string | null): number | null => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

type DeveloperStatusPayload = {
  project_id?: string | null;
  session_id?: string | null;
  job_id?: string | null;
  status?: string | null;
  step_key?: string | null;
  label?: string | null;
  message?: string | null;
  reason?: string | null;
  detail?: string | null;
  progress?: number | null;
  index_mode?: 'full' | 'incremental' | string | null;
  changed_count?: number | null;
  deleted_count?: number | null;
  indexed_path_count?: number | null;
  status_steps?: DeveloperStatusStep[];
};

const developerProgressMessageId = (
  projectId: string,
  sessionId: string | null | undefined,
): string => `developer-processing-${projectId}-${sessionId || 'project'}`;

const developerStatusMatches = (
  payload: DeveloperStatusPayload,
  projectId: string,
  activeSessionId: string | null,
): boolean => (
  payload.project_id === projectId
  && (!activeSessionId || !payload.session_id || payload.session_id === activeSessionId)
);

const isDeveloperProgressMessage = (
  message: ChatMessage,
  projectId: string,
  sessionId: string | null | undefined,
): boolean => (
  message.id === developerProgressMessageId(projectId, sessionId)
  || (
    message.id.startsWith(`developer-processing-${projectId}-`)
    && (!sessionId || message.session_id === sessionId)
  )
);

const inferDeveloperProgress = (status: string, message: string, progress?: number | null): number => {
  if (typeof progress === 'number' && Number.isFinite(progress)) {
    return Math.max(0, Math.min(100, progress));
  }

  const lowerMessage = message.toLowerCase();
  if (status === 'indexing' || lowerMessage.includes('index') || lowerMessage.includes('graph')) return 62;
  if (status === 'syncing' || lowerMessage.includes('sync')) return 44;
  if (lowerMessage.includes('classif')) return 18;
  if (lowerMessage.includes('plan') || lowerMessage.includes('analyz') || lowerMessage.includes('inspect')) return 92;

  return 36;
};

const developerStatusMessage = (payload: DeveloperStatusPayload, status: string): string => {
  if (payload.message) return payload.message;
  if (status === 'indexing' && payload.index_mode === 'incremental') {
    const changed = payload.changed_count ?? 0;
    const deleted = payload.deleted_count ?? 0;
    return `Developer Mode is incrementally updating the code graph (${changed} changed, ${deleted} deleted).`;
  }
  if (status === 'indexing' && payload.index_mode === 'full') {
    return 'Developer Mode is indexing the full code graph.';
  }
  return 'Developer Mode is preparing the response...';
};

const normalizeDeveloperStatusSteps = (steps: DeveloperStatusStep[] | undefined): DeveloperStatusStep[] => (
  Array.isArray(steps)
    ? [...steps].sort((left, right) => (
      (left.order_index ?? 0) - (right.order_index ?? 0)
      || (Number.isFinite(Date.parse(left.started_at || '')) ? Date.parse(left.started_at || '') : 0)
      - (Number.isFinite(Date.parse(right.started_at || '')) ? Date.parse(right.started_at || '') : 0)
    ))
    : []
);

const latestRunningStatusStep = (steps: readonly DeveloperStatusStep[]): DeveloperStatusStep | undefined => (
  [...steps].reverse().find((step) => (step.status || '').toLowerCase() === 'running')
);

const latestStatusStep = (steps: readonly DeveloperStatusStep[]): DeveloperStatusStep | undefined => (
  latestRunningStatusStep(steps) || steps[steps.length - 1]
);

const latestTerminalStatusStep = (steps: readonly DeveloperStatusStep[]): DeveloperStatusStep | undefined => (
  steps[steps.length - 1]
);

const statusStepCompletedAtMs = (step: DeveloperStatusStep | undefined): number | null => (
  timestampMs(step?.ended_at) ?? timestampMs(step?.started_at)
);

const terminalProgressCompletedAtMs = (message: ChatMessage): number | null => {
  const steps = normalizeDeveloperStatusSteps(message.developer_status_steps);
  const latest = latestTerminalStatusStep(steps);
  if (latest) return statusStepCompletedAtMs(latest);
  return timestampMs(message.updated_at) ?? timestampMs(message.created_at);
};

const progressDismissDelayMs = (completedAtMs?: number | null): number => {
  if (completedAtMs === null || completedAtMs === undefined) return DEVELOPER_PROGRESS_DISMISS_DELAY_MS;
  const elapsedMs = Math.max(0, Date.now() - completedAtMs);
  return Math.max(0, DEVELOPER_PROGRESS_DISMISS_DELAY_MS - elapsedMs);
};

export const isTerminalDeveloperProgressMessage = (message: ChatMessage): boolean => {
  if (message.author === 'user' || message.progress === null || message.progress === undefined) return false;
  const status = (message.developer_status || '').toLowerCase();
  return TERMINAL_DEVELOPER_STATUSES.has(status) || message.progress >= 100;
};

export const isExpiredTerminalDeveloperProgressMessage = (
  message: ChatMessage,
  nowMs = Date.now(),
): boolean => {
  if (!isTerminalDeveloperProgressMessage(message)) return false;
  const completedAtMs = terminalProgressCompletedAtMs(message);
  return completedAtMs !== null && nowMs - completedAtMs >= DEVELOPER_PROGRESS_DISMISS_DELAY_MS;
};

const clampDeveloperProgress = (progress: number): number => (
  Math.max(0, Math.min(100, progress))
);

const numericStepProgress = (step: DeveloperStatusStep | undefined): number | undefined => (
  typeof step?.progress === 'number' && Number.isFinite(step.progress)
    ? clampDeveloperProgress(step.progress)
    : undefined
);

const nextDeveloperProgress = (
  existing: ChatMessage | undefined,
  progress: number,
  nextJobId?: string | null,
): number => {
  if (!existing) return progress;
  const existingProgress = existing.progress ?? 0;
  const isNewJob = Boolean(nextJobId && existing.developer_job_id && existing.developer_job_id !== nextJobId);
  if (isNewJob || (existingProgress >= 100 && progress < 100)) return progress;
  return Math.max(existingProgress, progress);
};

const useDeveloperProgressMessages = (
  projectId: string | null,
  activeSessionId: string | null,
  chatState: ChatState,
  streamingState: StreamingState,
) => {
  const { setMessages } = chatState;
  const {
    setCurrentStreamingMsgId,
    setIsAiTyping,
    setStreamingDisplay,
  } = streamingState;
  const dismissTimersRef = useRef<Map<string, number>>(new Map());

  const clearProgressDismissTimer = useCallback((messageId: string) => {
    const timer = dismissTimersRef.current.get(messageId);
    if (timer !== undefined) window.clearTimeout(timer);
    dismissTimersRef.current.delete(messageId);
  }, []);

  const scheduleProgressDismiss = useCallback((
    messageId: string,
    jobId?: string | null,
    completedAtMs?: number | null,
  ) => {
    clearProgressDismissTimer(messageId);
    const timer = window.setTimeout(() => {
      dismissTimersRef.current.delete(messageId);
      setMessages((prev) => prev.filter((message) => {
        if (message.id !== messageId) return true;
        if (jobId && message.developer_job_id !== jobId) return true;
        return !isTerminalDeveloperProgressMessage(message);
      }));
    }, progressDismissDelayMs(completedAtMs));
    dismissTimersRef.current.set(messageId, timer);
  }, [clearProgressDismissTimer, setMessages]);

  useEffect(() => () => {
    dismissTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    dismissTimersRef.current.clear();
  }, []);

  useEffect(() => {
    if (!projectId) return;
    const nowMs = Date.now();
    if (!chatState.messages.some((message) => (
      (!message.project_id || message.project_id === projectId)
      && (!activeSessionId || !message.session_id || message.session_id === activeSessionId)
      && isExpiredTerminalDeveloperProgressMessage(message, nowMs)
    ))) {
      return;
    }
    setMessages((prev) => prev.filter((message) => !(
      (!message.project_id || message.project_id === projectId)
      && (!activeSessionId || !message.session_id || message.session_id === activeSessionId)
      && isExpiredTerminalDeveloperProgressMessage(message, nowMs)
    )));
  }, [activeSessionId, chatState.messages, projectId, setMessages]);

  useEffect(() => {
    if (!projectId || !activeSessionId) return undefined;
    let cancelled = false;

    projectService.getDeveloperModeStatusHistory(projectId, { sessionId: activeSessionId })
      .then((steps) => {
        if (cancelled) return;
        const statusSteps = normalizeDeveloperStatusSteps(steps);
        const latest = latestStatusStep(statusSteps);
        if (!latest) return;

        const content = latest.message || latest.label || 'Developer Mode is preparing the response...';
        const latestStatus = (latest.status || '').toLowerCase();
        const isRunning = latestStatus === 'running';
        const progress = inferDeveloperProgress(
          isRunning ? 'running' : 'completed',
          content,
          isRunning ? numericStepProgress(latest) : 100,
        );
        const id = developerProgressMessageId(projectId, activeSessionId);
        const now = new Date().toISOString();
        if (isRunning) {
          clearProgressDismissTimer(id);
        } else {
          const completedAtMs = statusStepCompletedAtMs(latest);
          if (completedAtMs !== null && Date.now() - completedAtMs >= DEVELOPER_PROGRESS_DISMISS_DELAY_MS) {
            setMessages((prev) => prev.filter((message) => !(
              isDeveloperProgressMessage(message, projectId, activeSessionId)
              && isExpiredTerminalDeveloperProgressMessage(message)
            )));
            return;
          }
          scheduleProgressDismiss(id, latest.job_id, completedAtMs);
        }

        setMessages((prev) => {
          const existing = prev.find((message) => message.id === id);
          const nextProgress = nextDeveloperProgress(existing, progress, latest.job_id);
          const next: ChatMessage = {
            id,
            user_id: '',
            project_id: projectId,
            session_id: activeSessionId,
            content,
            author: 'assistant',
            created_at: existing?.created_at || latest.started_at || now,
            updated_at: now,
            progress: nextProgress,
            developer_status_steps: statusSteps,
            developer_job_id: latest.job_id ?? existing?.developer_job_id ?? null,
            developer_status: isRunning ? 'running' : latestStatus || 'completed',
          };

          return existing
            ? prev.map((message) => (message.id === id ? next : message))
            : [...prev, next];
        });
      })
      .catch(() => undefined);

    return () => { cancelled = true; };
  }, [
    activeSessionId,
    clearProgressDismissTimer,
    projectId,
    scheduleProgressDismiss,
    setMessages,
  ]);

  useEffect(() => {
    if (!projectId) return undefined;

    return eventService.subscribe('developer_mode_status', (data: unknown) => {
      const payload = data as DeveloperStatusPayload;
      if (!developerStatusMatches(payload, projectId, activeSessionId)) return;

      const sessionId = payload.session_id ?? activeSessionId ?? null;
      const status = (payload.status || '').toLowerCase();

      if (TERMINAL_DEVELOPER_STATUSES.has(status)) {
        const statusSteps = normalizeDeveloperStatusSteps(payload.status_steps);
        if (statusSteps.length) {
          const content = developerStatusMessage(payload, status);
          const id = developerProgressMessageId(projectId, sessionId);
          const now = new Date().toISOString();
          const completedAtMs = statusStepCompletedAtMs(latestTerminalStatusStep(statusSteps)) ?? Date.now();
          scheduleProgressDismiss(id, payload.job_id, completedAtMs);
          setMessages((prev) => {
            const existing = prev.find((message) => message.id === id);
            const next: ChatMessage = {
              id,
              user_id: '',
              project_id: projectId,
              session_id: sessionId,
              content,
              author: 'assistant',
              created_at: existing?.created_at || statusSteps[0]?.started_at || now,
              updated_at: now,
              progress: 100,
              developer_status_steps: statusSteps,
              developer_job_id: payload.job_id ?? existing?.developer_job_id ?? null,
              developer_status: status,
            };

            return existing
              ? prev.map((message) => (message.id === id ? next : message))
              : [...prev, next];
          });
        } else {
          setMessages((prev) => (
            prev.filter((message) => !isDeveloperProgressMessage(message, projectId, sessionId))
          ));
        }

        return;
      }

      if (!ACTIVE_DEVELOPER_STATUSES.has(status)) return;

      const content = developerStatusMessage(payload, status);
      const id = developerProgressMessageId(projectId, sessionId);
      const now = new Date().toISOString();
      const statusSteps = normalizeDeveloperStatusSteps(payload.status_steps);
      const progress = numericStepProgress(latestRunningStatusStep(statusSteps))
        ?? inferDeveloperProgress(status, content, payload.progress);
      clearProgressDismissTimer(id);

      setIsAiTyping(false);
      setStreamingDisplay('');
      setCurrentStreamingMsgId(null);

      setMessages((prev) => {
        const existing = prev.find((message) => message.id === id);
        const nextProgress = nextDeveloperProgress(existing, progress, payload.job_id);
        const next: ChatMessage = {
          id,
          user_id: '',
          project_id: projectId,
          session_id: sessionId,
          content,
          author: 'assistant',
          created_at: existing?.created_at || now,
          updated_at: now,
          progress: nextProgress,
          developer_status_steps: statusSteps.length ? statusSteps : existing?.developer_status_steps,
          developer_job_id: payload.job_id ?? existing?.developer_job_id ?? null,
          developer_status: status,
        };

        return existing
          ? prev.map((message) => (message.id === id ? next : message))
          : [...prev, next];
      });
    });
  }, [
    activeSessionId,
    clearProgressDismissTimer,
    projectId,
    scheduleProgressDismiss,
    setCurrentStreamingMsgId,
    setIsAiTyping,
    setMessages,
    setStreamingDisplay,
  ]);
};

export const useChatEffects = (
  projectId: string | null,
  activeSessionId: string | null,
  visibleMessages: ChatMessage[],
  chatState: ChatState,
  streamingState: StreamingState,
  refsFull: StreamingRefsFull,
  triggerInitialAIStream: () => Promise<void>
) => {
  useEffect(() => {
    if (projectId && visibleMessages.length > 0) {
      const hasAssistant = visibleMessages.some((m) => m.author === 'assistant');
      const latestUser = [...visibleMessages].reverse().find((m) => m.author === 'user');
      const isDocumentUpload = latestUserMessageHasDocumentAttachments(visibleMessages);

      if (latestUser && !latestUser.prompt_scope?.project_rename_request
        && !latestUser.prompt_scope?.reward_claim
        && !hasAssistant && !isDocumentUpload && !refsFull.hasTriggeredInitialAIRef.current) {
        refsFull.setHasTriggeredInitialAI(true);
        triggerInitialAIStream();
      }
    }
  }, [projectId, visibleMessages, triggerInitialAIStream, refsFull]);

  // Auto-scroll to bottom when new messages arrive.
  // Deferred via rAF so the DOM has rendered the new message before we scroll.
  // When the chat container is hidden (user is on Design/Tasks tab) we do NOT
  // drop the request — we record it as pending and fire when the container
  // becomes visible again, so switching back to chat always lands at the bottom.
  const pendingScrollRef = useRef(false);
  const pendingScrollBehaviorRef = useRef<ScrollBehavior>('smooth');
  const prevVisibleLenRef = useRef(0);

  useEffect(() => {
    pendingScrollRef.current = false;
    pendingScrollBehaviorRef.current = 'auto';
    prevVisibleLenRef.current = 0;
  }, [projectId, activeSessionId]);

  // eslint-disable-next-line max-statements
  useEffect(() => {
    const previousLength = prevVisibleLenRef.current;
    const nextLength = visibleMessages.length;

    if (nextLength <= previousLength) {
      prevVisibleLenRef.current = nextLength;

      return undefined;
    }
    const isInitialLoad = previousLength === 0;
    const appendedMessages = previousLength > 0
      ? visibleMessages.slice(previousLength)
      : [];
    const onlyRewardClaimReceipts = appendedMessages.length > 0
      && appendedMessages.every(isRewardClaimReceipt);

    prevVisibleLenRef.current = nextLength;
    if (!refsFull.shouldAutoScrollRef.current || onlyRewardClaimReceipts) return undefined;
    const container = refsFull.chatContainerRef?.current;
    const behavior: ScrollBehavior = isInitialLoad ? 'auto' : 'smooth';

    if (container && container.offsetParent === null) {
      // Hidden right now — defer until visible
      pendingScrollRef.current = true;
      pendingScrollBehaviorRef.current = behavior;
      return undefined;
    }
    window.requestAnimationFrame(() => {
      refsFull.messagesEndRef.current?.scrollIntoView({ behavior });
    });
    return undefined;
  }, [refsFull, visibleMessages]);

  // Fire deferred scroll when the container becomes visible again
  useEffect(() => {
    const container = refsFull.chatContainerRef?.current;
    if (!container) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && pendingScrollRef.current) {
          const behavior = pendingScrollBehaviorRef.current;

          pendingScrollRef.current = false;
          window.requestAnimationFrame(() => {
            refsFull.messagesEndRef.current?.scrollIntoView({ behavior });
          });
        }
      },
      { threshold: 0.01 },
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [refsFull]);


  useSystemMessageSubscription(projectId, activeSessionId, chatState);
  usePlanningMessageRecovery(projectId, activeSessionId, chatState.setMessages);
  useSuggestedActionMetadataSubscription(projectId, activeSessionId, chatState);
  useDeveloperStreamSubscription(projectId, activeSessionId, streamingState, refsFull);
  useDeveloperProgressMessages(projectId, activeSessionId, chatState, streamingState);
  useDeveloperStatusCleanup(projectId, activeSessionId, streamingState);

  // Scroll to bottom when agent suggestion buttons appear or programmatically requested
  useEffect(() => {
    const handler = () => {
      // An explicit scroll request must always win, even if the user had
      // scrolled up (which disarms auto-scroll for regular new messages).
      refsFull.shouldAutoScrollRef.current = true;

      const container = refsFull.chatContainerRef?.current;

      if (container && container.offsetParent === null) {
        // Chat is hidden — defer until visible (same path as message auto-scroll)
        pendingScrollRef.current = true;
        return;
      }
      // Double-rAF: first lets React re-render, second lets the DOM layout settle
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          refsFull.messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        });
      });
    };

    window.addEventListener('suggested_agents', handler);
    window.addEventListener('scroll_to_chat_bottom', handler);

    return () => {
      window.removeEventListener('suggested_agents', handler);
      window.removeEventListener('scroll_to_chat_bottom', handler);
    };
  }, [refsFull]);

  useEffect(() => () => {
    refsFull.abortStream();
    refsFull.clearTokenInterval();
  }, [refsFull]);
};
